import React, { memo, useEffect, useState } from "react";

import { isLocalStorageAvailable } from "@services/IsLocalStorageAvailable";
import { langFromPathname } from "@services/LangFromPathname";

import { usePrismic } from "@hooks/usePrismic";
import { useLocalisation } from "@hooks/useLocalisation";

interface Props {
  children: React.ReactElement;
}

const CookieAccessGuard = memo((props: Props): React.ReactElement | null => {
  const { getPrismicEntry, isApiReady } = usePrismic();
  const hasStorageAccess = isLocalStorageAvailable();
  const { country } = useLocalisation();

  // indicates that current route is a help page - to avoid infinite redirections
  const [isHelpPage, setIsHelpPage] = useState(false);

  useEffect(() => {
    if (hasStorageAccess || !isApiReady) {
      return;
    }

    (async () => {
      const lang = langFromPathname();
      const prismicArticles = await getPrismicEntry({
        contentType: "help_article",
        lang,
        country,
        tags: ["cookie-help"],
      });

      const slug = prismicArticles?.[0]?.data?.slug?.[0]?.text;
      const redirectTarget = slug
        ? `/${lang}/${slug}`
        : `/${lang}/help-section/how-to-enable-cookies`;

      if (redirectTarget === location.pathname) {
        setIsHelpPage(true);
        return;
      }

      location.pathname = redirectTarget;
    })();
  }, [getPrismicEntry, hasStorageAccess, isApiReady]);

  return hasStorageAccess || isHelpPage ? props.children : null;
});

CookieAccessGuard.displayName = "CookieAccessGuard";

export { CookieAccessGuard };
